import { FormConditionalService } from '@client';
import { PARTNER_PREFIX } from '@shared';
import { useMemo } from 'react';

export const usePrefillCitationsForForm = (
  formPrefillData: {
    prefillData: PrefillData[];
    citations: PrefillCitations[];
  },
  conditionalService: FormConditionalService,
  currentSubsection: PolicyFormSubSection | null,
  formData: PolicyFormSubmissionData | null,
) => {
  return useMemo(() => {
    // Early return if no data or subsection
    if (
      !currentSubsection ||
      !formPrefillData?.prefillData?.length ||
      !formPrefillData?.citations?.length
    ) {
      return {};
    }

    const subsectionQuestions =
      currentSubsection.questionGroups.flatMap(group => group.questions) || [];

    const visiblePrefillData = formPrefillData.prefillData.filter(prefillItem => {
      if (!subsectionQuestions.includes(prefillItem.questionRef)) {
        return false;
      }
      return conditionalService.isQuestionRefVisible(prefillItem.questionRef);
    });

    // Create citations map per questionRef
    const citationsPerQuestion: Record<string, PrefillCitationDef> = {};

    visiblePrefillData.forEach(prefillItem => {
      const questionRef = prefillItem.questionRef;

      // Initialize citation arrays for this question
      const partnerCitations: PrefillCitations[] = [];
      const modifiedCitations: PrefillCitations[] = [];
      const generalCitations: PrefillCitations[] = [];

      // Get citations for this specific question's source refs
      if (prefillItem.sourceQuestionRefs && Array.isArray(prefillItem.sourceQuestionRefs)) {
        const relevantCitations = formPrefillData.citations.filter(citation => {
          return prefillItem.sourceQuestionRefs?.includes(citation.ref);
        });

        relevantCitations.forEach(citation => {
          // Check if the answer has been modified in formData
          let isModified = false;
          if (formData && formData[questionRef] !== undefined) {
            const currentAnswer = formData[questionRef];
            const prefillAnswer = prefillItem.answer;

            // Deep comparison for objects/arrays, simple comparison for primitives
            if (JSON.stringify(currentAnswer) !== JSON.stringify(prefillAnswer)) {
              isModified = true;
            }
          }

          if (isModified) {
            modifiedCitations.push(citation);
          }
          // Check if this citation's ref starts with PARTNER_PREFIX
          else if (citation.ref.startsWith(PARTNER_PREFIX)) {
            partnerCitations.push(citation);
            return;
          } else {
            // This citation is neither partner-specific nor modified
            generalCitations.push(citation);
          }
        });
      }

      // Remove duplicates from each array using Map
      const dedupGeneral = Array.from(new Map(generalCitations.map(c => [c.ref, c])).values());
      const dedupPartner = Array.from(new Map(partnerCitations.map(c => [c.ref, c])).values());
      const dedupModified = Array.from(new Map(modifiedCitations.map(c => [c.ref, c])).values());

      citationsPerQuestion[questionRef] = {
        generalCitations: dedupGeneral,
        partnerCitations: dedupPartner,
        modifiedCitations: dedupModified,
      };
    });

    return citationsPerQuestion;
  }, [formPrefillData, conditionalService, currentSubsection, formData]);
};
